import styled from 'styled-components';

const StatisticsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, ${props => props.$color1} 0%, ${props => props.$color2} 100%);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  color: white;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
`;

const StatTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px;
  opacity: 0.95;
`;

const StatValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const StatSubtext = styled.div`
  font-size: 14px;
  opacity: 0.9;
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const CategoryStatsContainer = styled.div`
  margin-top: 20px;
`;

const CategoryStatTitle = styled.h4`
  font-size: 18px;
  color: #333;
  margin: 0 0 16px;
  font-weight: 600;
`;

const CategoryStatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  margin-bottom: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid ${props => props.$color};
  transition: all 0.2s;

  &:hover {
    background-color: #e9ecef;
    transform: translateX(4px);
  }
`;

const CategoryName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #333;
`;

const CategoryStats = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const CategorySuccessRate = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.$color};
`;

const CategoryDetails = styled.div`
  font-size: 13px;
  color: #666;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const getSuccessRateColor = (rate) => {
  if (rate >= 80) return '#28a745';
  if (rate >= 60) return '#ffc107';
  return '#dc3545';
};

const getCategoryColor = (index) => {
  const colors = ['#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8', '#6f42c1'];
  return colors[index % colors.length];
};

const StatisticsBlock = ({ programs, sessions }) => {
  // 전체 통계 계산
  const calculateOverallStats = () => {
    if (!sessions || sessions.length === 0) {
      return {
        totalTrials: 0,
        totalSuccess: 0,
        totalFailure: 0,
        successRate: 0,
        totalSessions: 0
      };
    }

    const stats = sessions.reduce((acc, session) => {
      acc.totalTrials += session.repetitionCount || 0;
      acc.totalSuccess += session.successCount || 0;
      acc.totalFailure += session.failureCount || 0;
      return acc;
    }, { totalTrials: 0, totalSuccess: 0, totalFailure: 0 });

    return {
      ...stats,
      successRate: stats.totalTrials > 0 ? (stats.totalSuccess / stats.totalTrials * 100).toFixed(1) : 0,
      totalSessions: sessions.length
    };
  };

  // 대주제별 통계 계산
  const calculateCategoryStats = () => {
    if (!programs || programs.length === 0 || !sessions || sessions.length === 0) {
      return [];
    }

    // 프로그램 ID를 대주제와 매핑
    const programCategoryMap = {};
    programs.forEach(p => {
      programCategoryMap[p.program.id] = p.program.majorCategory;
    });

    // 대주제별로 세션 데이터 그룹화
    const categoryStats = {};

    sessions.forEach(session => {
      const category = programCategoryMap[session.programId];
      if (!category) return;

      if (!categoryStats[category]) {
        categoryStats[category] = {
          totalTrials: 0,
          totalSuccess: 0,
          totalFailure: 0,
          sessionCount: 0
        };
      }

      categoryStats[category].totalTrials += session.repetitionCount || 0;
      categoryStats[category].totalSuccess += session.successCount || 0;
      categoryStats[category].totalFailure += session.failureCount || 0;
      categoryStats[category].sessionCount += 1;
    });

    // 배열로 변환하고 성공률 계산
    return Object.entries(categoryStats).map(([category, stats]) => ({
      category,
      ...stats,
      successRate: stats.totalTrials > 0 ? (stats.totalSuccess / stats.totalTrials * 100).toFixed(1) : 0
    })).sort((a, b) => b.successRate - a.successRate);
  };

  const overallStats = calculateOverallStats();
  const categoryStats = calculateCategoryStats();

  if (!sessions || sessions.length === 0) {
    return (
      <EmptyMessage>
        세션 기록이 없어 통계를 표시할 수 없습니다.
      </EmptyMessage>
    );
  }

  return (
    <>
      <StatisticsContainer>
        {/* 전체 성공률 */}
        <StatCard $color1="#667eea" $color2="#764ba2">
          <StatTitle>전체 성공률</StatTitle>
          <StatValue>{overallStats.successRate}%</StatValue>
          <StatSubtext>
            <span>총 {overallStats.totalSessions}개 세션</span>
            <span>{overallStats.totalTrials}회 시도</span>
          </StatSubtext>
        </StatCard>

        {/* 총 성공 횟수 */}
        <StatCard $color1="#11998e" $color2="#38ef7d">
          <StatTitle>총 성공 횟수</StatTitle>
          <StatValue>{overallStats.totalSuccess}회</StatValue>
          <StatSubtext>
            <span>전체 시도 중</span>
            <span>{overallStats.totalTrials}회</span>
          </StatSubtext>
        </StatCard>

        {/* 총 실패 횟수 */}
        <StatCard $color1="#ee0979" $color2="#ff6a00">
          <StatTitle>총 실패 횟수</StatTitle>
          <StatValue>{overallStats.totalFailure}회</StatValue>
          <StatSubtext>
            <span>전체 시도 중</span>
            <span>{overallStats.totalTrials}회</span>
          </StatSubtext>
        </StatCard>
      </StatisticsContainer>

      {/* 대주제별 통계 */}
      {categoryStats.length > 0 && (
        <CategoryStatsContainer>
          <CategoryStatTitle>대주제별 성공률</CategoryStatTitle>
          {categoryStats.map((stat, index) => (
            <CategoryStatItem key={stat.category} $color={getCategoryColor(index)}>
              <CategoryName>{stat.category}</CategoryName>
              <CategoryStats>
                <CategorySuccessRate $color={getSuccessRateColor(parseFloat(stat.successRate))}>
                  {stat.successRate}%
                </CategorySuccessRate>
                <CategoryDetails>
                  성공 {stat.totalSuccess}회 / 실패 {stat.totalFailure}회
                  <br />
                  (총 {stat.totalTrials}회 시도, {stat.sessionCount}개 세션)
                </CategoryDetails>
              </CategoryStats>
            </CategoryStatItem>
          ))}
        </CategoryStatsContainer>
      )}
    </>
  );
};

export default StatisticsBlock;
